import { FunctionComponent, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import {
	StaffTicketDetail,
	TicketCategory,
	TicketPriority,
	TicketStatus,
	TicketUserRef,
	UpdateStaffTicketRequest,
} from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { Icon } from "@/components/Icon/Icon";

import { ticketUserName } from "../../../utils/staffTicketRowMeta";
import { TriageOutcome } from "../hooks/useTicketTriage";
import { THEN_SET_STATUSES } from "../utils/staffComposer";
import { isNoOpTriage } from "../utils/staffTriage";
import { RailHeading } from "./RailSection";

interface TicketTriageSectionProps {
	ticket: StaffTicketDetail;
	assignees: TicketUserRef[];
	isTriaging: boolean;
	triage: (fields: UpdateStaffTicketRequest) => Promise<TriageOutcome>;
	onOutcome: (outcome: TriageOutcome, fields: UpdateStaffTicketRequest) => void;
}

const UNASSIGNED = "UNASSIGNED";

const Field: FunctionComponent<{ label: string; children: ReactNode }> = ({ label, children }) => (
	<div className="flex flex-col gap-1">
		<span className="text-[11px] text-text-meta">{label}</span>
		{children}
	</div>
);

/**
 * The option a value names, or nothing. Never substitute `options[0]`: for the assignee that is
 * "Unassigned", so a value the list does not hold would render as — and, through the write below,
 * *become* — unassigned (PREC-4069). An unresolved value leaves the dropdown on its placeholder,
 * which is the honest answer while the roster is still loading.
 */
const selected = (
	options: AutoCompleteItem[],
	id: AutoCompleteItem["id"]
): AutoCompleteItem | undefined => options.find((option) => option.id === id);

/**
 * The roster only lists current staff (PREC-4068 narrowed it to our own company), so a ticket
 * assigned before that — or to someone since moved — has an assignee the list does not contain.
 * `selected` would then fall back to the first option and the rail would read "Unassigned" for an
 * assigned ticket. Keep the current assignee in the options so it shows who it actually is.
 */
const withCurrentAssignee = (
	assignees: TicketUserRef[],
	current: TicketUserRef | null
): TicketUserRef[] =>
	current && !assignees.some((user) => user.id === current.id)
		? [...assignees, current]
		: assignees;

/**
 * The mockup's "Triage": one Dropdown per field, each PATCHing only its own field (story AC), and
 * "Mark as resolved" (decision 8: no confirmation, hidden once RESOLVED / CLOSED).
 *
 * Every dropdown here is uncontrolled (`defaultValue`), because its select handler writes: a
 * controlled Dropdown replays `handleSelect` from an effect on each render, which turned this rail
 * into a PATCH loop (PREC-4069). `apply` still drops a pick equal to the ticket's own value, so a
 * re-selection asks and reports nothing.
 */
export const TicketTriageSection: FunctionComponent<TicketTriageSectionProps> = ({
	ticket,
	assignees,
	isTriaging,
	triage,
	onOutcome,
}) => {
	const { t } = useTranslation();

	const statusOptions: AutoCompleteItem[] = useMemo(
		() =>
			THEN_SET_STATUSES.map((status) => ({
				id: status,
				title: t(`support.staff.status.${status}`),
			})),
		[t]
	);
	const assigneeOptions: AutoCompleteItem[] = useMemo(
		() => [
			{ id: UNASSIGNED, title: t("support.staff.detail.rail.unassigned") },
			...withCurrentAssignee(assignees, ticket.assignee).map((user) => ({
				id: user.id,
				title: ticketUserName(user),
			})),
		],
		[assignees, ticket.assignee, t]
	);
	const priorityOptions: AutoCompleteItem[] = useMemo(
		() =>
			Object.values(TicketPriority).map((priority) => ({
				id: priority,
				title: t(`support.staff.priority.${priority}`),
			})),
		[t]
	);
	const categoryOptions: AutoCompleteItem[] = useMemo(
		() =>
			Object.values(TicketCategory).map((category) => ({
				id: category,
				title: t(`support.category.${category}`),
			})),
		[t]
	);

	const apply = async (fields: UpdateStaffTicketRequest) => {
		if (isNoOpTriage(ticket, fields)) {
			return;
		}

		onOutcome(await triage(fields), fields);
	};

	const dropdown = (
		testId: string,
		options: AutoCompleteItem[],
		current: AutoCompleteItem["id"],
		toFields: (item: AutoCompleteItem) => UpdateStaffTicketRequest
	) => (
		// `defaultValue` only, never `value`: a controlled Dropdown re-fires `handleSelect` from an
		// effect on every render, and this handler PATCHes — so the rail would write on render
		// rather than on a click (PREC-4069). `defaultValue` resyncs the display without calling
		// back, so only a real pick reaches `apply`.
		<Dropdown
			classNameDropdown="w-full"
			data={options}
			defaultValue={selected(options, current)}
			disabled={isTriaging}
			floating={true}
			handleSelect={(item: AutoCompleteItem) => void apply(toFields(item))}
			size={ButtonSize.XS}
			testId={testId}
		/>
	);

	const isOpenForResolve =
		ticket.status !== TicketStatus.RESOLVED && ticket.status !== TicketStatus.CLOSED;

	return (
		<>
			<RailHeading>{t("support.staff.detail.rail.triage")}</RailHeading>
			<div className="mt-1.5 flex flex-col gap-2.5">
				<Field label={t("support.staff.detail.rail.status")}>
					{dropdown("triage-status", statusOptions, ticket.status, (item) => ({
						status: item.id as TicketStatus,
					}))}
				</Field>
				<Field label={t("support.staff.detail.rail.assignee")}>
					{dropdown(
						"triage-assignee",
						assigneeOptions,
						ticket.assigneeUserId ?? UNASSIGNED,
						(item) => ({ assigneeUserId: item.id === UNASSIGNED ? null : Number(item.id) })
					)}
				</Field>
				<div className="grid grid-cols-2 gap-2.5">
					<Field label={t("support.staff.detail.rail.priority")}>
						{dropdown("triage-priority", priorityOptions, ticket.priority, (item) => ({
							priority: item.id as TicketPriority,
						}))}
					</Field>
					<Field label={t("support.staff.detail.rail.category")}>
						{dropdown("triage-category", categoryOptions, ticket.category, (item) => ({
							category: item.id as TicketCategory,
						}))}
					</Field>
				</div>
				{isOpenForResolve && (
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={isTriaging}
						image={<Icon className="mr-1.5" icon={faCheck} />}
						size={ButtonSize.XS}
						title={t("support.staff.detail.rail.markResolved")}
						type="button"
						onClick={() => void apply({ status: TicketStatus.RESOLVED })}
					/>
				)}
			</div>
		</>
	);
};
